/**
 * `createApiReviewsSource` — the `seed-mgmt-api` human review queue over the
 * shared `ApiClient`.
 *
 * Everything the platform DISCOVERS about an address, a contract, a token or a
 * catalog row is quarantined rather than written: the pipeline's catalog flush
 * (`pipeline-mgmt-api` `build_review_store`) and the transaction ingest's
 * discovery (`transactions-mgmt-api` `catalog_discovery`) both enqueue onto ONE
 * queue, under the default `QUARANTINE` policy, and a seed run with `review_all`
 * queues every row it assembles. Nothing leaves that queue without a person, and
 * until this port existed the only way to be that person was a CLI. A proposal
 * nobody approves is a catalog row that never exists.
 *
 * Canonical `/seed-reviews` paths are rewritten onto `internal/seed-mgmt-api`
 * (`/v0/seed/reviews`) by `RESOURCE_REWRITES`, so region routing, failover, auth
 * and envelope unwrap are all inherited from the one HTTP brain.
 *
 * ## What the wire actually carries
 *
 * `review_item_to_api` (seed-mgmt-api `review/wire.py`) is the whole shape, and
 * three parts of it are what make a decision possible rather than a coin toss:
 *
 * - `field_path` / `existing_value` / `proposed_value` — the DIFF, when the
 *   proposal is about one field of an existing row. Absent on a whole-row
 *   proposal, where `payload` is the row being proposed and there is nothing to
 *   diff against.
 * - `evidence` — `commons.review.build_evidence_view`'s projection: per-source
 *   confidence, who dissented, how stale each assertion is, and the arithmetic
 *   (`base_confidence + agreement_delta = headline_confidence`) that produced
 *   the headline. A bare 0.52 is unactionable; this is what makes it falsifiable.
 * - `reason` / `reason_detail` / `providers` / `source` — WHY it is in the queue
 *   at all (below threshold, a conflict with a human's own edit, a rotation, a
 *   quarantined provider).
 *
 * ## Paging
 *
 * `walkPages` is deliberately NOT used. That helper drains `{items,
 * next_cursor}`, and this route answers `{reviews, count, cursor}` — and more
 * importantly the queue is a work list, not a picker: draining every page of a
 * `review_all` run before drawing anything is the wrong trade. So the cursor is
 * exposed and the surface pages explicitly. The API's own warning is honoured
 * either way: a page can come back SHORT and still have more behind it (the
 * `target_kind` filter is applied after the limit), so the stop condition is a
 * null cursor and never an empty page.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";

import { asNumber, asRecord, asString, asStringList, isRecord, rowsAt } from "./wire";

/** Canonical collection path; `RESOURCE_REWRITES` maps it to the service. */
export const REVIEWS_PATH = "/seed-reviews";

/**
 * How many candidates one bulk decision may carry.
 *
 * `seed_mgmt_api_shared.review.service.BULK_DECISION_LIMIT`. Bounded by the
 * Lambda timeout rather than by taste — the decisions run sequentially and an
 * approval does a validate plus a live write — so exceeding it does not fail
 * cleanly, it times out halfway with no way to tell which half landed. The
 * surface states the cap rather than silently truncating a selection.
 */
export const BULK_DECISION_LIMIT = 100;

/**
 * `commons.review.ReviewStatus`. `approved` is terminal (its snapshot has
 * already been written live); `rejected` is the only one that reopens.
 */
export const REVIEW_STATUSES = ["pending", "approved", "rejected", "escalated"] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

/** The `?status=` filters the list route accepts — the statuses plus "all". */
export const REVIEW_STATUS_FILTERS = [...REVIEW_STATUSES, "all"] as const;
export type ReviewStatusFilter = (typeof REVIEW_STATUS_FILTERS)[number];

/** `commons.review.ReviewTargetKind` — what kind of thing is under review. */
export const REVIEW_TARGET_KINDS = [
  "address",
  "token",
  "nft",
  "abi",
  "proxy",
  "entity",
  "label",
  "tag",
  "group",
  "entity_mapping",
  "assignment",
  "catalog_field",
  "ml_prediction",
] as const;
export type ReviewTargetKind = (typeof REVIEW_TARGET_KINDS)[number];

/** `commons.review.ReviewReason` — why it needs a human. */
export const REVIEW_REASONS = [
  "low_confidence",
  "human_overwrite",
  "conflict",
  "quarantine",
  "rotation",
  "hitl",
  "trigger",
] as const;
export type ReviewReason = (typeof REVIEW_REASONS)[number];

/** One source's assertion, as `build_evidence_view` projects it. */
export interface ReviewEvidenceSource {
  kind: string;
  id: string | null;
  /** This source's own confidence — NOT the item's headline. */
  confidence: number | null;
  assertedFields: string[];
  /** The engine lowers the headline for a dissenter; this is which one. */
  conflict: boolean;
  reasons: string[];
  url: string | null;
  lastSeenAt: number | null;
  ageDays: number | null;
  explain: string | null;
}

/**
 * Why the confidence is what it is.
 *
 * `null` when the producer had no evidence to show — which is a different
 * statement from "the sources agreed", and the surface must not draw an empty
 * table for it. `build_evidence_view` returns `{}` for an empty set, so the
 * emptiness is on the wire and is preserved here rather than invented.
 */
export interface ReviewEvidence {
  sources: ReviewEvidenceSource[];
  distinctSources: number | null;
  conflicts: number | null;
  /** The best single trusted source, before the agreement nudge. */
  baseConfidence: number | null;
  /** The nudge itself: `base + delta = headline`. */
  agreementDelta: number | null;
  /** What the engine actually published, when the view carries it. */
  headlineConfidence: number | null;
  explanation: string | null;
}

export interface ReviewItem {
  reviewId: string;
  status: ReviewStatus;
  targetKind: ReviewTargetKind;
  /** `chain#address`, a catalog id, a prediction id — kind decides. */
  targetId: string;
  orgIdTenantId: string | null;
  reason: ReviewReason;
  reasonDetail: string;
  /**
   * The headline score, or `null` when the row carries none.
   *
   * `null`, never `0` and never `1`: the producer's default is `0.0` and a
   * catalog proposal's own default is `1`, so a fallback either way would print
   * a number nobody asserted on the one surface where a reader is deciding
   * whether to trust the proposal. See `reviewConfidence` in `./reviewDiff`.
   */
  confidence: number | null;
  providers: string[];
  evidence: ReviewEvidence | null;
  /** Dot-path when this is a single-field conflict; `null` for a whole row. */
  fieldPath: string | null;
  existingValue: unknown;
  proposedValue: unknown;
  /** The DDB-ready snapshot approval writes live. */
  payload: Record<string, unknown>;
  payloadTable: string | null;
  /** Producing subsystem: `seed` | `tx_pipeline` | `third_party` | `ml`. */
  source: string | null;
  assertedAt: number | null;
  updatedAt: number | null;
  reviewerId: string | null;
  reviewNotes: string | null;
  metadata: Record<string, unknown>;
}

/** One page of the queue. `cursor` null means this is the last one. */
export interface ReviewPage {
  items: ReviewItem[];
  cursor: string | null;
}

export interface ListReviewsParams {
  status?: ReviewStatusFilter;
  targetKind?: ReviewTargetKind;
  limit?: number;
  cursor?: string;
  signal?: AbortSignal;
}

/** `PromotionResult` — what one approve / reject actually did. */
export interface ReviewDecisionResult {
  reviewId: string;
  /** The resulting status, or `"missing"` when the id is unknown. */
  status: string;
  /** Whether the snapshot reached its canonical table. */
  wroteLive: boolean;
  table: string | null;
  detail: string;
}

/** `BulkDecisionResult` — one candidate's outcome inside a bulk decision. */
export interface BulkReviewResult {
  reviewId: string;
  ok: boolean;
  status: string | null;
  wroteLive: boolean | null;
  table: string | null;
  error: string | null;
  /**
   * The exception class name — `ConflictError` (somebody already decided it)
   * and `ValidationError` (the row no longer validates) mean different things to
   * a reader deciding whether to retry, and a message alone makes them guess.
   */
  errorKind: string | null;
}

/**
 * `BulkDecision` — the totals plus every item's outcome.
 *
 * The route answers **200 even for a partial batch**, which is the one thing a
 * caller must not miss: a blanket 4xx would say nothing about the items that DID
 * land, so `failed > 0` on a successful response is normal and has to be read.
 */
export interface BulkDecisionOutcome {
  decision: string;
  requested: number;
  decided: number;
  failed: number;
  results: BulkReviewResult[];
}

export interface DecideReviewInput {
  decision: "approved" | "rejected";
  notes?: string;
  /**
   * Reviewer edits applied before the live write — *approve-with-edits*.
   *
   * Re-validated server-side against the row's canonical model, with the
   * identity / key fields locked, so this cannot re-point a proposal at a
   * different row.
   */
  payload?: Record<string, unknown>;
}

export interface ReviewsSource {
  /** One page of the queue, oldest first. Drain on {@link ReviewPage.cursor}. */
  list(params?: ListReviewsParams): Promise<ReviewPage>;
  /** One candidate, with its payload, its diff and its evidence. */
  get(reviewId: string): Promise<ReviewItem>;
  /** Approve (optionally with edits) or reject one candidate. */
  decide(reviewId: string, input: DecideReviewInput): Promise<ReviewDecisionResult>;
  /** Reopen a REJECTED candidate for re-review. Approved items are refused. */
  reopen(reviewId: string, notes?: string): Promise<ReviewItem>;
  /** Save edits to a pending proposal without deciding it yet. */
  saveEdits(reviewId: string, payload: Record<string, unknown>): Promise<ReviewItem>;
  /**
   * Decide up to {@link BULK_DECISION_LIMIT} candidates in one call.
   *
   * Rejects a longer list HERE rather than sending it: the server's refusal
   * would be a 400 the reader cannot act on, and the surface can state the cap.
   */
  bulkDecide(
    reviewIds: readonly string[],
    decision: "approved" | "rejected",
    notes?: string,
  ): Promise<BulkDecisionOutcome>;
  /** Permanently drop a DECIDED candidate (queue hygiene). Needs `seed:admin`. */
  remove(reviewId: string): Promise<void>;
}

function asEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  const raw = asString(value);
  return (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

function evidenceSourceFromWire(raw: Record<string, unknown>): ReviewEvidenceSource {
  return {
    kind: asString(raw.kind, "unknown"),
    id: raw.id == null ? null : String(raw.id),
    confidence: asNumber(raw.confidence),
    assertedFields: asStringList(raw.asserted_fields),
    conflict: raw.conflict === true,
    reasons: asStringList(raw.reasons),
    url: raw.url == null ? null : String(raw.url),
    lastSeenAt: asNumber(raw.last_seen_at),
    ageDays: asNumber(raw.age_days),
    explain: raw.explain == null ? null : String(raw.explain),
  };
}

/**
 * The evidence view, or `null` when there is none.
 *
 * `build_evidence_view` answers `{}` for an empty set precisely so a reader sees
 * nothing rather than an empty table, so `{}` is mapped to `null` and not to a
 * bag of zeroes — "no sources" and "zero agreement between the sources" are
 * different claims.
 */
export function reviewEvidenceFromWire(raw: unknown): ReviewEvidence | null {
  if (!isRecord(raw)) return null;
  const sources = rowsAt(raw, "sources").map(evidenceSourceFromWire);
  const hasAnything =
    sources.length > 0 ||
    raw.base_confidence != null ||
    raw.headline_confidence != null ||
    Boolean(raw.explanation);
  if (!hasAnything) return null;
  return {
    sources,
    distinctSources: asNumber(raw.distinct_sources),
    conflicts: asNumber(raw.conflicts),
    baseConfidence: asNumber(raw.base_confidence),
    agreementDelta: asNumber(raw.agreement_delta),
    headlineConfidence: asNumber(raw.headline_confidence),
    explanation: raw.explanation == null ? null : String(raw.explanation),
  };
}

/**
 * One wire row as a `ReviewItem`.
 *
 * Exported for the test and for any surface that receives a row by another
 * route: the mapping is the contract with `review_item_to_api`, and a second
 * copy of it is how a renamed field comes to render as `undefined`.
 */
export function reviewFromWire(raw: Record<string, unknown>): ReviewItem {
  return {
    reviewId: asString(raw.review_id),
    // An unrecognised status falls back to `pending`, the only value that does
    // not license an action: reading an unknown lifecycle state as `approved`
    // would hide a row that still needs a person, and as `rejected` would offer
    // a reopen that the server refuses.
    status: asEnum(raw.status, REVIEW_STATUSES, "pending"),
    targetKind: asEnum(raw.target_kind, REVIEW_TARGET_KINDS, "catalog_field"),
    targetId: asString(raw.target_id),
    orgIdTenantId: raw.org_id_tenant_id == null ? null : String(raw.org_id_tenant_id),
    reason: asEnum(raw.reason, REVIEW_REASONS, "low_confidence"),
    reasonDetail: asString(raw.reason_detail),
    // `asNumber` maps an absent field to `null`, which is the point — see
    // `ReviewItem.confidence`.
    confidence: asNumber(raw.confidence),
    providers: asStringList(raw.providers),
    evidence: reviewEvidenceFromWire(raw.evidence),
    fieldPath: raw.field_path == null ? null : String(raw.field_path),
    // Deliberately uncoerced: a proposed value is whatever the producer
    // asserted — a string, a number, a nested object — and stringifying it here
    // would lose the shape the diff has to render.
    existingValue: raw.existing_value ?? null,
    proposedValue: raw.proposed_value ?? null,
    payload: asRecord(raw.payload),
    payloadTable: raw.payload_table == null ? null : String(raw.payload_table),
    source: raw.source == null ? null : String(raw.source),
    assertedAt: asNumber(raw.asserted_at),
    updatedAt: asNumber(raw.updated_at),
    reviewerId: raw.reviewer_id == null ? null : String(raw.reviewer_id),
    reviewNotes: raw.review_notes == null ? null : String(raw.review_notes),
    metadata: asRecord(raw.metadata),
  };
}

function decisionResultFromWire(raw: Record<string, unknown>): ReviewDecisionResult {
  return {
    reviewId: asString(raw.review_id),
    status: asString(raw.status, "missing"),
    wroteLive: raw.wrote_live === true,
    table: raw.table == null ? null : String(raw.table),
    detail: asString(raw.detail),
  };
}

function bulkResultFromWire(raw: Record<string, unknown>): BulkReviewResult {
  return {
    reviewId: asString(raw.review_id),
    ok: raw.ok === true,
    status: raw.status == null ? null : String(raw.status),
    wroteLive: raw.wrote_live == null ? null : raw.wrote_live === true,
    table: raw.table == null ? null : String(raw.table),
    error: raw.error == null ? null : String(raw.error),
    errorKind: raw.error_kind == null ? null : String(raw.error_kind),
  };
}

/**
 * The bulk outcome. `results` is read per item, never inferred from the totals:
 * the call answers 200 for a partial batch, so `decided`/`failed` are a summary
 * of the list and not a substitute for it.
 */
export function bulkOutcomeFromWire(raw: Record<string, unknown>): BulkDecisionOutcome {
  const results = rowsAt(raw, "results").map(bulkResultFromWire);
  return {
    decision: asString(raw.decision),
    requested: asNumber(raw.requested) ?? results.length,
    decided: asNumber(raw.decided) ?? results.filter((r) => r.ok).length,
    failed: asNumber(raw.failed) ?? results.filter((r) => !r.ok).length,
    results,
  };
}

export function createApiReviewsSource(client: ApiClient): ReviewsSource {
  const itemPath = (reviewId: string) => `${REVIEWS_PATH}/${encodeURIComponent(reviewId)}`;

  /**
   * Every decision changes what the queue lists, so the collection read is
   * dropped from cache by fragment: the list is keyed by status + kind + cursor,
   * and an exact-URL invalidate would only reach the one page the caller can
   * name — leaving the other status tabs asserting a row that has just moved.
   */
  const invalidate = (reviewId?: string): void => {
    client.invalidateMatching(REVIEWS_PATH);
    if (reviewId) client.invalidate(itemPath(reviewId));
  };

  return {
    async list({
      status = "pending",
      targetKind,
      limit,
      cursor,
      signal,
    }: ListReviewsParams = {}): Promise<ReviewPage> {
      const query: Record<string, string> = { status };
      if (targetKind) query.target_kind = targetKind;
      if (limit != null) query.limit = String(limit);
      if (cursor) query.cursor = cursor;

      const raw = asRecord(
        unwrapData(await client.get<unknown>(REVIEWS_PATH, { query, signal })),
      );
      const next = raw.cursor;
      return {
        items: rowsAt(raw, "reviews").map(reviewFromWire),
        // Present and non-null means there is more, EVEN IF this page came back
        // short — `target_kind` filters after the limit. A blank string is not a
        // cursor, so it is normalised to null rather than re-sent as `?cursor=`.
        cursor: typeof next === "string" && next ? next : null,
      };
    },

    async get(reviewId: string): Promise<ReviewItem> {
      const raw = asRecord(unwrapData(await client.get<unknown>(itemPath(reviewId))));
      return reviewFromWire(asRecord(raw.review));
    },

    async decide(reviewId, input): Promise<ReviewDecisionResult> {
      const body: Record<string, unknown> = { decision: input.decision };
      if (input.notes) body.notes = input.notes;
      // Only on an approval. `payload` on a rejection is meaningless — nothing
      // is written — and sending it would record edits against a discarded row.
      if (input.payload && input.decision === "approved") body.payload = input.payload;

      const raw = asRecord(unwrapData(await client.patch<unknown>(itemPath(reviewId), body)));
      invalidate(reviewId);
      return decisionResultFromWire(asRecord(raw.result));
    },

    async reopen(reviewId, notes): Promise<ReviewItem> {
      const body: Record<string, unknown> = { decision: "reopen" };
      if (notes) body.notes = notes;
      const raw = asRecord(unwrapData(await client.patch<unknown>(itemPath(reviewId), body)));
      invalidate(reviewId);
      return reviewFromWire(asRecord(raw.review));
    },

    async saveEdits(reviewId, payload): Promise<ReviewItem> {
      // No `decision` — the route reads that as "save edits in place".
      const raw = asRecord(
        unwrapData(await client.patch<unknown>(itemPath(reviewId), { payload })),
      );
      invalidate(reviewId);
      return reviewFromWire(asRecord(raw.review));
    },

    async bulkDecide(reviewIds, decision, notes): Promise<BulkDecisionOutcome> {
      // Blanks dropped and duplicates collapsed BEFORE the cap is measured, the
      // same way the server does it: a repeated id would otherwise be decided
      // twice and the second attempt reported as a conflict — a failure for
      // something that did in fact happen — and would eat a slot under the cap.
      const ids = Array.from(new Set(reviewIds.map((id) => id.trim()).filter(Boolean)));
      if (ids.length === 0) {
        throw new Error("Select at least one proposal to decide.");
      }
      if (ids.length > BULK_DECISION_LIMIT) {
        throw new Error(
          `One bulk decision covers at most ${BULK_DECISION_LIMIT} proposals; ${ids.length} were selected.`,
        );
      }
      const body: Record<string, unknown> = { review_ids: ids, decision };
      if (notes) body.notes = notes;

      const raw = asRecord(unwrapData(await client.patch<unknown>(REVIEWS_PATH, body)));
      invalidate();
      return bulkOutcomeFromWire(asRecord(raw.bulk));
    },

    async remove(reviewId: string): Promise<void> {
      unwrapData(await client.delete<void>(itemPath(reviewId)));
      invalidate(reviewId);
    },
  };
}
